import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import { toast } from "sonner";
import { ArrowRight, Handshake, Users, Globe, Award, Building2, Mail, Phone, User } from "lucide-react";
import { WEB3FORMS_ACCESS_KEY } from "../config";

const PARTNERS_IMAGE = "/assets/images/partners/all-partners.png";

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
};

const AnimatedSection = ({ children, className = "" }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  
  return (
    <motion.section
      ref={ref}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={staggerContainer}
      className={className}
    >
      {children}
    </motion.section>
  );
};

// Partnership Benefits
const partnershipBenefits = [
  {
    icon: Users,
    title: "Access to 14,000+ School Leaders",
    description: "Connect with decision-makers from schools across India"
  },
  {
    icon: Globe,
    title: "Pan-India Visibility",
    description: "Showcase your brand at events in 50+ cities"
  },
  {
    icon: Award,
    title: "Premium Brand Positioning",
    description: "Associate with India's most trusted education leadership platform"
  },
  {
    icon: Building2,
    title: "B2B Networking",
    description: "Direct engagement opportunities with school management"
  }
];

// Partnership Form Component
const PartnershipForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          access_key: WEB3FORMS_ACCESS_KEY,
          subject: "New Partnership Inquiry - GSLC",
          from_name: "GSLC Partners",
          ...formData,
          type: 'partnership'
        }),
      });
      
      const result = await response.json();
      if (result.success) {
        toast.success("Thank you for your interest! We'll be in touch shortly.");
        setFormData({ name: '', email: '', company: '', phone: '', message: '' });
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6" data-testid="partnership-form">
      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">Your Name *</label>
          <div className="relative">
            <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="text"
              name="name"
              required
              value={formData.name}
              onChange={handleChange}
              data-testid="partner-name-input"
              className="w-full pl-12 pr-4 py-3 rounded-xl border-2 border-slate-200 focus:border-red-500 focus:outline-none transition-colors"
              placeholder="John Doe"
            />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">Company Name *</label>
          <div className="relative">
            <Building2 className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="text"
              name="company"
              required
              value={formData.company}
              onChange={handleChange}
              data-testid="partner-company-input"
              className="w-full pl-12 pr-4 py-3 rounded-xl border-2 border-slate-200 focus:border-red-500 focus:outline-none transition-colors"
              placeholder="Your Company"
            />
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">Email Address *</label>
          <div className="relative">
            <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="email"
              name="email"
              required
              value={formData.email}
              onChange={handleChange}
              data-testid="partner-email-input"
              className="w-full pl-12 pr-4 py-3 rounded-xl border-2 border-slate-200 focus:border-red-500 focus:outline-none transition-colors"
              placeholder="you@company.com"
            />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">Phone Number</label>
          <div className="relative">
            <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              data-testid="partner-phone-input"
              className="w-full pl-12 pr-4 py-3 rounded-xl border-2 border-slate-200 focus:border-red-500 focus:outline-none transition-colors"
              placeholder="+91 98765 43210"
            />
          </div>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-slate-700 mb-2">Tell us about your partnership interest *</label>
        <textarea
          name="message"
          required
          rows={4}
          value={formData.message}
          onChange={handleChange}
          data-testid="partner-message-input"
          className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-red-500 focus:outline-none transition-colors resize-none"
          placeholder="What kind of partnership are you looking for? Event sponsorship, content collaboration, product showcasing..."
        />
      </div>

      <motion.button
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        type="submit"
        disabled={isSubmitting}
        data-testid="partner-submit-btn"
        className="w-full btn-gradient py-4 text-lg disabled:opacity-50"
      >
        {isSubmitting ? "Submitting..." : "Submit Partnership Inquiry"}
      </motion.button>
    </form>
  );
};

const PartnersPage = () => {
  return (
    <div className="pt-24">
      {/* Hero */}
      <AnimatedSection className="section-padding bg-gradient-to-br from-yellow-50 via-white to-blue-50">
        <div className="container-custom">
          <motion.div variants={fadeInUp} className="max-w-4xl mx-auto text-center">
            <span className="font-accent text-red-600 text-sm tracking-[0.2em] uppercase">Collaborate With Us</span>
            <h1 className="font-heading text-4xl md:text-6xl font-semibold mt-4 mb-6 text-slate-800">
              Partner With <span className="gradient-text">GSLC</span>
            </h1>
            <p className="text-lg text-slate-600 leading-relaxed max-w-2xl mx-auto">
              Join hands with India's largest school leadership consortium. Reach 14,000+ decision-makers 
              and position your brand at the forefront of educational transformation.
            </p>
          </motion.div>
        </div>
      </AnimatedSection>

      {/* Our Partners Showcase */}
      <AnimatedSection className="section-padding bg-gradient-to-br from-slate-100 via-white to-slate-100">
        <div className="container-custom">
          <motion.div variants={fadeInUp} className="text-center mb-12">
            <span className="font-accent text-red-600 text-sm tracking-[0.2em] uppercase">Trusted By The Best</span>
            <h2 className="font-heading text-3xl md:text-5xl font-medium mt-4 text-slate-800">
              Brands Who Have <span className="gradient-text">Partnered</span> With Us
            </h2>
          </motion.div>

          <motion.div variants={fadeInUp} className="max-w-5xl mx-auto">
            <img 
              src={PARTNERS_IMAGE} 
              alt="Our Partners - Leading education and technology brands" 
              className="w-full rounded-2xl"
              data-testid="partners-showcase-image"
            />
          </motion.div>
        </div>
      </AnimatedSection>

      {/* Why Partner Section */}
      <AnimatedSection className="section-padding">
        <div className="container-custom">
          <motion.div variants={fadeInUp} className="text-center mb-12">
            <span className="font-accent text-red-600 text-sm tracking-[0.2em] uppercase">Benefits</span>
            <h2 className="font-heading text-3xl md:text-5xl font-medium mt-4 text-slate-800">
              Why Partner With <span className="gradient-text">GSLC</span>?
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {partnershipBenefits.map((benefit, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="card-light rounded-2xl p-6 text-center hover-lift"
              >
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-yellow-100 to-red-100 flex items-center justify-center mx-auto mb-4">
                  <benefit.icon className="w-7 h-7 text-red-600" />
                </div>
                <h3 className="font-heading text-lg text-slate-800 mb-2">{benefit.title}</h3>
                <p className="text-slate-500 text-sm">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </AnimatedSection>

      {/* Partnership Types */}
      <AnimatedSection className="section-padding bg-gradient-to-br from-blue-50 via-white to-yellow-50">
        <div className="container-custom">
          <motion.div variants={fadeInUp} className="text-center mb-12">
            <span className="font-accent text-red-600 text-sm tracking-[0.2em] uppercase">Opportunities</span>
            <h2 className="font-heading text-3xl md:text-5xl font-medium mt-4 text-slate-800">
              Partnership <span className="gradient-text">Opportunities</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <motion.div variants={fadeInUp} className="card-light rounded-2xl p-8 border-t-4 border-yellow-500">
              <h3 className="font-heading text-2xl text-slate-800 mb-4">Event Sponsorship</h3>
              <ul className="space-y-3 text-slate-600 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-yellow-500 mt-1">•</span>
                  Title sponsorship at flagship events
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-yellow-500 mt-1">•</span>
                  Exhibition stalls and product demos
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-yellow-500 mt-1">•</span>
                  Branding across all event materials
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-yellow-500 mt-1">•</span>
                  Speaking opportunities
                </li>
              </ul>
            </motion.div>

            <motion.div variants={fadeInUp} className="card-light rounded-2xl p-8 border-t-4 border-red-600">
              <h3 className="font-heading text-2xl text-slate-800 mb-4">Content Collaboration</h3>
              <ul className="space-y-3 text-slate-600 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-red-500 mt-1">•</span>
                  Webinar co-hosting
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-500 mt-1">•</span>
                  Newsletter features
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-500 mt-1">•</span>
                  Social media campaigns
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-red-500 mt-1">•</span>
                  Case study publications
                </li>
              </ul>
            </motion.div>

            <motion.div variants={fadeInUp} className="card-light rounded-2xl p-8 border-t-4 border-blue-700">
              <h3 className="font-heading text-2xl text-slate-800 mb-4">Annual Partnership</h3>
              <ul className="space-y-3 text-slate-600 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-blue-500 mt-1">•</span>
                  Year-round brand visibility
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-500 mt-1">•</span>
                  Priority event access
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-500 mt-1">•</span>
                  Exclusive networking sessions
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-500 mt-1">•</span>
                  Custom engagement programs
                </li>
              </ul>
            </motion.div>
          </div>
        </div>
      </AnimatedSection>

      {/* Partnership Inquiry Form */}
      <AnimatedSection className="section-padding">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <motion.div variants={fadeInUp} className="text-center mb-10">
              <span className="font-accent text-red-600 text-sm tracking-[0.2em] uppercase">Get In Touch</span>
              <h2 className="font-heading text-3xl md:text-5xl font-medium mt-4 text-slate-800">
                Become a <span className="gradient-text">Partner</span>
              </h2>
              <p className="text-slate-500 mt-4">
                Fill out the form below and our partnerships team will reach out to discuss opportunities.
              </p>
            </motion.div>

            <motion.div variants={fadeInUp} className="card-light rounded-3xl p-8 md:p-10">
              <PartnershipForm />
            </motion.div>
          </div>
        </div>
      </AnimatedSection>

      {/* CTA */}
      <AnimatedSection className="section-padding bg-gradient-to-r from-slate-50 via-white to-slate-50">
        <div className="container-custom">
          <motion.div 
            variants={fadeInUp}
            className="bg-gradient-to-r from-yellow-500 via-red-600 to-blue-800 rounded-3xl p-12 text-center relative overflow-hidden"
          >
            <div className="absolute inset-0 bg-black/10" />
            <div className="relative z-10">
              <Handshake className="w-16 h-16 text-white mx-auto mb-6" />
              <h2 className="font-heading text-3xl md:text-4xl text-white mb-6">
                Ready to Collaborate?
              </h2>
              <p className="text-white/90 mb-8 max-w-xl mx-auto">
                Let's create impactful experiences together. Reach out to explore partnership possibilities.
              </p>
              <a
                href="mailto:pankaj@gslc.co.in"
                className="inline-flex items-center gap-2 px-10 py-4 rounded-full bg-white text-blue-900 font-semibold hover:bg-yellow-400 transition-colors"
              >
                Email Us Directly <ArrowRight size={18} />
              </a>
            </div>
          </motion.div>
        </div>
      </AnimatedSection>
    </div>
  );
};

export default PartnersPage;
